import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { SecondaryLink as Link } from 'components/link';
import SanityBlockRenderer, {
  ISerializers,
  SanityBlockContentLinkProps,
} from 'components/sanity-block-renderer';
import useUserInSameRegion from 'hooks/use-user-in-same-region';
import { useCustomizableContent } from 'state/customizable-content';
import { useLocale } from 'state/intl';
import { primitive } from 'styles/constants/primitives';
import { getLocaleTextBlock, parseSanityBlockMultipleValues } from 'utils/sanity';

const RewardsUnavailableSorry = styled.span`
  font-family: ${Styles.fontFamily.base};
  font-size: 1.5rem;
  text-transform: capitalize;
  color: ${primitive.th.$coffeeBrown};
  font-weight: ${Styles.fontWeight.heavy};
`;

const RewardsUnavailable = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${Styles.zIndex.overlay};
  text-align: center;
  background: ${Styles.color.background};
  padding: 20% ${primitive.$spacing4} 0;
`;

const StyledLink = styled(Link)`
  font-weight: ${Styles.fontWeight.heavy};
  font-size: 1rem;
`;

const BlockRenderer = styled(SanityBlockRenderer)`
  & > p {
    color: ${primitive.th.$coffeeBrown};
    margin: 1rem 0;
  }
  p:last-child {
    font-weight: ${Styles.fontWeight.heavy};
  }
`;

const RewardsUnavailableText = () => {
  const { formatMessage } = useIntl();
  const { loyaltyContent } = useCustomizableContent();
  const { locale, readableRegions } = useLocale();
  const { loyaltyRegion } = useUserInSameRegion();

  const rewardsUnavailableBlockTextContent = parseSanityBlockMultipleValues(
    getLocaleTextBlock(loyaltyContent, 'rewardsUnavailableBlockText'),
    {
      language: locale,
      country: readableRegions[loyaltyRegion] || formatMessage({ id: 'differentRegion' }),
    }
  );

  const linkSerializer: ISerializers = {
    marks: {
      link: ({ children = [], mark = {} }: SanityBlockContentLinkProps) => {
        const [linkText] = children;
        const { href } = mark;
        return href && linkText ? <StyledLink to={href}>{linkText || ''}</StyledLink> : null;
      },
    },
  };

  return (
    <BlockRenderer content={rewardsUnavailableBlockTextContent} serializers={linkSerializer} />
  );
};

const RewardsUnavailableOverlay = () => {
  const { formatMessage } = useIntl();
  const { isInSameRegion } = useUserInSameRegion();

  if (isInSameRegion) {
    return null;
  }

  return (
    <RewardsUnavailable>
      <RewardsUnavailableSorry>
        {formatMessage({ id: 'loyaltyUnavailableInRegionOops' })}
      </RewardsUnavailableSorry>
      <RewardsUnavailableText />
    </RewardsUnavailable>
  );
};

export default RewardsUnavailableOverlay;
